import React from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    IconButton,
    Button,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha } from '@mui/material/styles';

const OPERATORS = [
    { value: 'contains', label: 'Contains', needsValue: true, supportsFlags: true },
    { value: 'not_contains', label: 'Not Contains', needsValue: true, supportsFlags: true },
    { value: 'exists', label: 'Exists', needsValue: false, supportsFlags: false },
    { value: 'not_exists', label: 'Not Exists', needsValue: false, supportsFlags: false },
    { value: 'gt', label: '>', needsValue: true, supportsFlags: false },
    { value: 'gte', label: '≥', needsValue: true, supportsFlags: false },
    { value: 'lt', label: '<', needsValue: true, supportsFlags: false },
    { value: 'lte', label: '≤', needsValue: true, supportsFlags: false },
];

function JsonFilterGroup({ title, filters, onChange, keyOptions }) {
    const handleAddFilter = () => {
        onChange([...filters, { key: '', operator: 'contains', value: '', matchCase: false, words: false }]);
    };

    const handleRemoveFilter = (index) => {
        // For first filter (index 0), just clear the values instead of removing
        if (index === 0) {
            const newFilters = [...filters];
            newFilters[0] = { key: '', operator: 'contains', value: '', matchCase: false, words: false };
            onChange(newFilters);
        } else {
            // For other filters, remove the row
            const newFilters = filters.filter((_, i) => i !== index);
            onChange(newFilters);
        }
    };

    const handleFilterChange = (index, field, value) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], [field]: value };
        onChange(newFilters);
    };

    const toggleMatchCase = (index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], matchCase: !newFilters[index].matchCase };
        onChange(newFilters);
    };

    const toggleWords = (index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], words: !newFilters[index].words };
        onChange(newFilters);
    };

    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    color: '#3b3c3b',
                    textTransform: 'uppercase',
                    fontSize: 12,
                    letterSpacing: 0.5,
                }}
            >
                {title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filters.map((filter, index) => {
                    const operator = filter.operator || 'contains';
                    const operatorDef = OPERATORS.find(op => op.value === operator) || OPERATORS[0];
                    const showValue = operatorDef.needsValue;
                    const showFlags = operatorDef.supportsFlags;
                    
                    return (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                backgroundColor: alpha('#f5f5f5', 0.5),
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: alpha('#3b3c3b', 0.1),
                            }}
                        >
                            <TextField
                                select
                                size="small"
                                value={filter.key || ''}
                                onChange={(e) => handleFilterChange(index, 'key', e.target.value)}
                                placeholder="Select key"
                                sx={{ minWidth: 180 }}
                                SelectProps={{
                                    displayEmpty: true,
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>Select key</em>
                                </MenuItem>
                                {keyOptions.map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {key}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                size="small"
                                value={operator}
                                onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                                sx={{ minWidth: 130 }}
                            >
                                {OPERATORS.map((op) => (
                                    <MenuItem key={op.value} value={op.value}>
                                        {op.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {showValue && (
                                <TextField
                                    size="small"
                                    placeholder="Value"
                                    value={filter.value || ''}
                                    onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                                    sx={{ flexGrow: 1, minWidth: 200 }}
                                />
                            )}

                            {showFlags && (
                                <>
                                    <Tooltip title="Match case">
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleMatchCase(index)}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                border: '1px solid',
                                                borderRadius: 0.5,
                                                borderColor: filter.matchCase ? 'primary.main' : alpha('#3b3c3b', 0.3),
                                                backgroundColor: filter.matchCase ? 'primary.main' : 'transparent',
                                                color: filter.matchCase ? '#fff' : alpha('#3b3c3b', 0.6),
                                                '&:hover': {
                                                    backgroundColor: filter.matchCase
                                                        ? 'primary.dark'
                                                        : alpha('#3b3c3b', 0.05),
                                                },
                                                fontSize: 11,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Aa
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Whole words">
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleWords(index)}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                border: '1px solid',
                                                borderRadius: 0.5,
                                                borderColor: filter.words ? 'primary.main' : alpha('#3b3c3b', 0.3),
                                                backgroundColor: filter.words ? 'primary.main' : 'transparent',
                                                color: filter.words ? '#fff' : alpha('#3b3c3b', 0.6),
                                                '&:hover': {
                                                    backgroundColor: filter.words
                                                        ? 'primary.dark'
                                                        : alpha('#3b3c3b', 0.05),
                                                },
                                                fontSize: 11,
                                                fontWeight: 700,
                                            }}
                                        >
                                            W
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}

                            <Tooltip title={index === 0 ? "Clear filter" : "Remove filter"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveFilter(index)}
                                        disabled={index === 0 && !filter.key && !filter.value}
                                        sx={{ 
                                            color: alpha('#d32f2f', 0.7),
                                            '&.Mui-disabled': {
                                                color: alpha('#3b3c3b', 0.3),
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                })}

                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddFilter}
                    size="small"
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                    }}
                >
                    Add filter
                </Button>
            </Box>
        </Box>
    );
}

export default JsonFilterGroup;

